import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { FileText, Plus, X, DollarSign, Calendar, Tag, Trash2 } from 'lucide-react';
import Swal from 'sweetalert2';
import UseAxiosSecure from '../hooks/UseAxiosSecure';
import UseAuth from '../hooks/UseAuth';

const ExpenseLedger = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { register, handleSubmit, reset } = useForm();
    const axiosSecure = UseAxiosSecure();
    const queryClient = useQueryClient();
    const { activeBranchId } = UseAuth();

    // FETCH EXPENSES
    const { data: expenses = [], isLoading } = useQuery({
        queryKey: ['expenses', activeBranchId],
        queryFn: async () => {
            const res = await axiosSecure.get('/expenses');
            return res.data?.data || [];
        },
        enabled: !!activeBranchId
    });

    // ADD EXPENSE MUTATION
    const addExpenseMutation = useMutation({
        mutationFn: async (data) => {
            const res = await axiosSecure.post('/expenses', data);
            return res.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['expenses', activeBranchId]);
            setIsModalOpen(false);
            reset();
            Swal.fire({ icon: 'success', title: 'Expense Logged!', background: '#1a103c', color: '#fff', timer: 1500, showConfirmButton: false });
        }
    });

    // DELETE EXPENSE MUTATION
    const deleteExpenseMutation = useMutation({
        mutationFn: async (id) => {
            const res = await axiosSecure.delete(`/expenses/${id}`);
            return res.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['expenses', activeBranchId]);
        }
    });

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Delete this expense?',
            text: "This will affect your Net Profit calculation.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#3b82f6',
            confirmButtonText: 'Yes, delete it!',
            background: '#1a103c',
            color: '#fff'
        }).then((result) => {
            if (result.isConfirmed) {
                deleteExpenseMutation.mutate(id);
            }
        });
    };

    const onSubmit = (data) => {
        addExpenseMutation.mutate(data);
    };

    const totalExpenses = expenses.reduce((sum, exp) => sum + exp.amount, 0);

    if (!activeBranchId) return <div className="text-white text-center mt-20">Loading Ledger...</div>;

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            
            {/* HEADER */}
            <div className="bg-[#1a103c]/40 backdrop-blur-md p-6 rounded-3xl border border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                        <FileText className="text-orange-500" /> Utility & Extra Costs
                    </h2>
                    <p className="text-slate-400 text-sm mt-1">Track rent, bills, and operational expenses.</p>
                </div>
                <div className="flex items-center gap-4">
                    <div className="px-6 py-3 bg-black/20 border border-white/10 rounded-xl">
                        <span className="text-slate-400 text-xs font-bold uppercase block">Total Logged</span>
                        <span className="text-xl font-black text-orange-400">${totalExpenses.toFixed(2)}</span>
                    </div>
                    <button 
                        onClick={() => setIsModalOpen(true)}
                        className="px-6 py-3 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white rounded-xl font-bold flex items-center gap-2 transition-all shadow-lg active:scale-95 whitespace-nowrap"
                    >
                        <Plus size={18} /> Log Expense
                    </button>
                </div>
            </div>

            {/* EXPENSE TABLE */}
            <div className="bg-[#1a103c]/60 backdrop-blur-xl border border-white/5 rounded-3xl overflow-hidden shadow-2xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-white/10 text-xs uppercase tracking-widest text-slate-400 bg-white/5">
                                <th className="p-4 font-semibold">Date</th>
                                <th className="p-4 font-semibold">Category</th>
                                <th className="p-4 font-semibold">Description</th>
                                <th className="p-4 font-semibold">Amount</th>
                                <th className="p-4 font-semibold text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr><td colSpan="5" className="p-10 text-center"><div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto"></div></td></tr>
                            ) : expenses.length === 0 ? (
                                <tr><td colSpan="5" className="p-10 text-center text-slate-500">No expenses logged for this branch yet.</td></tr>
                            ) : (
                                expenses.map((exp) => (
                                    <tr key={exp._id} className="border-b border-white/5 hover:bg-white/5 transition-colors text-sm">
                                        <td className="p-4 text-white">
                                            <div className="flex items-center gap-2">
                                                <Calendar size={14} className="text-slate-400" />
                                                {new Date(exp.expenseDate).toLocaleDateString()}
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            <span className="px-3 py-1 bg-white/10 text-slate-300 rounded-lg text-xs font-bold uppercase tracking-wider flex items-center gap-1 w-max">
                                                <Tag size={12}/> {exp.category}
                                            </span>
                                        </td>
                                        <td className="p-4 text-slate-300 max-w-xs truncate" title={exp.description}>{exp.description}</td>
                                        <td className="p-4 font-black text-orange-400">${exp.amount.toFixed(2)}</td>
                                        <td className="p-4 text-right">
                                            <button onClick={() => handleDelete(exp._id)} className="p-2 bg-red-500/10 text-red-500 hover:bg-red-500/20 rounded-lg transition-colors">
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ADD EXPENSE MODAL */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsModalOpen(false)}></div>
                    <div className="relative w-full max-w-lg bg-[#1a103c] border border-white/10 rounded-[2rem] shadow-2xl overflow-hidden p-8">
                        <button onClick={() => setIsModalOpen(false)} className="absolute top-6 right-6 text-slate-400 hover:text-white transition-colors"><X size={24} /></button>
                        <h3 className="text-2xl font-bold text-white mb-6">Log New Expense</h3>
                        
                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Date</label>
                                    <input {...register("expenseDate", { required: true })} type="date" defaultValue={new Date().toISOString().split('T')[0]} className="w-full bg-[#0f0a1f] border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-orange-500" />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Amount ($)</label>
                                    <input {...register("amount", { required: true })} type="number" step="0.01" placeholder="0.00" className="w-full bg-[#0f0a1f] border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-orange-500" />
                                </div>
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Category</label>
                                <select {...register("category", { required: true })} className="w-full bg-[#0f0a1f] border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-orange-500">
                                    <option value="Utilities">Utilities (Water/Electricity)</option>
                                    <option value="Maintenance">Maintenance & Repairs</option>
                                    <option value="Supplies">General Supplies (Soap, Tape)</option>
                                    <option value="Rent">Rent</option>
                                    <option value="Marketing">Marketing</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Description</label>
                                <textarea {...register("description", { required: true })} placeholder="What was this expense for?" rows="2" className="w-full bg-[#0f0a1f] border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-orange-500 resize-none"></textarea>
                            </div>
                            
                            <button type="submit" disabled={addExpenseMutation.isLoading} className="w-full mt-4 py-4 rounded-xl bg-orange-600 text-white font-bold text-lg hover:bg-orange-500 active:scale-95 transition-all flex items-center justify-center gap-2">
                                {addExpenseMutation.isLoading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <DollarSign size={20} />}
                                Save Expense
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExpenseLedger;